// Shared pieces of the handheld app: header, scan field (scanner, keyboard or camera), quantity stepper, alerts,
// and the beep and buzz a wrong scan gets.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";

declare global {
  interface Window { BarcodeDetector?: new (o?: { formats?: string[] }) => { detect: (src: CanvasImageSource) => Promise<{ rawValue: string }[]> } }
}

// ---------- the chosen location, kept on this device ----------
const LOC_KEY = "wms.location";
export const getLocation = () => { try { return localStorage.getItem(LOC_KEY) ?? ""; } catch { return ""; } };
export const setLocation = (v: string) => { try { localStorage.setItem(LOC_KEY, v); } catch { /* private window */ } };

// ---------- feedback ----------
let audio: AudioContext | null = null;
function tone(freq: number, ms: number, when = 0) {
  try {
    audio ??= new AudioContext();
    const o = audio.createOscillator(), g = audio.createGain();
    o.frequency.value = freq; o.type = "square"; g.gain.value = 0.06;
    o.connect(g); g.connect(audio.destination);
    const t = audio.currentTime + when; o.start(t); o.stop(t + ms / 1000);
  } catch { /* no audio */ }
}
export const feedback = {
  ok: () => { tone(1320, 70); try { navigator.vibrate?.(30); } catch { /* */ } },
  bad: () => { tone(220, 160); tone(180, 220, 0.2); try { navigator.vibrate?.([90, 60, 90]); } catch { /* */ } },
  done: () => { tone(990, 80); tone(1480, 120, 0.1); try { navigator.vibrate?.(60); } catch { /* */ } },
};

// ---------- icons ----------
const paths: Record<string, ReactNode> = {
  back: <path d="M15 18l-6-6 6-6" />,
  close: <path d="M6 6l12 12M18 6 6 18" />,
  scan: <><path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2" /><path d="M7 8v8M10 8v8M13 8v8M17 8v8" /></>,
  camera: <><path d="M4 8h3l2-3h6l2 3h3v11H4z" /><circle cx="12" cy="13" r="3.5" /></>,
  move: <path d="M4 7h13l-3-3M20 17H7l3 3" />,
  pick: <><rect x="5" y="3" width="14" height="18" rx="2" /><path d="m9 12 2 2 4-4M9 7h6" /></>,
  check: <path d="m5 12 5 5 9-10" />,
  alert: <><circle cx="12" cy="12" r="9" /><path d="M12 7v6M12 16.5h.01" /></>,
  warn: <><path d="M12 3 2 20h20z" /><path d="M12 10v4M12 17h.01" /></>,
  chev: <path d="m9 6 6 6-6 6" />,
  down: <path d="m6 9 6 6 6-6" />,
  plus: <path d="M12 5v14M5 12h14" />,
  minus: <path d="M5 12h14" />,
  arrow: <path d="M5 12h14M13 6l6 6-6 6" />,
  home: <><path d="M3 21V9l9-6 9 6v12" /><path d="M9 21v-6h6v6" /></>,
  box: <><path d="M21 8 12 3 3 8v8l9 5 9-5z" /><path d="m3 8 9 5 9-5M12 13v8" /></>,
  search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>,
  refill: <><path d="M4 20h16" /><path d="M6 20V10h5v10M13 20V5h5v15" /><path d="M8.5 4v3M7 5.5 8.5 4 10 5.5" /></>,
  count: <path d="M6 5v14M10 5v14M14 5v14M18 5v14M3.5 15.5l17-6" />,
  quick: <><path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2" /><path d="M13 6l-4 7h4l-2 5 5-8h-4z" /></>,
};
export function I({ n, s = 22, w = 2 }: { n: keyof typeof paths; s?: number; w?: number }) {
  return <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={w} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[n]}</svg>;
}

// ---------- layout ----------
export function Header({ title, sub, back, right }: { title: string; sub?: string; back?: string | (() => void); right?: ReactNode }) {
  const btn = <I n="back" s={24} />;
  return (
    <header className="w-head">
      {typeof back === "string" ? <Link to={back} className="w-iconbtn" aria-label="Back">{btn}</Link>
        : back ? <button type="button" className="w-iconbtn" aria-label="Back" onClick={back}>{btn}</button> : <span style={{ width: 8 }} />}
      <div className="w-head-t"><div className="w-title">{title}</div>{sub && <div className="w-sub">{sub}</div>}</div>
      {right}
    </header>
  );
}
export const LocChip = ({ code }: { code: string }) => <span className="w-chip">{code}</span>;
export function Steps({ n, at }: { n: number; at: number }) {
  return <div className="w-steps" style={{ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }}>{Array.from({ length: n }, (_, i) => <span key={i} className={i < at ? "on" : i === at ? "now" : ""} />)}</div>;
}
export function Alert({ tone = "bad", title, children }: { tone?: "bad" | "warn" | "ok"; title: string; children?: ReactNode }) {
  return (
    <div className={`w-alert ${tone}`} role={tone === "bad" ? "alert" : "status"}>
      <I n={tone === "bad" ? "alert" : tone === "warn" ? "warn" : "check"} s={26} />
      <div><div className="w-alert-t">{title}</div>{children && <div className="w-alert-b">{children}</div>}</div>
    </div>
  );
}
export const Done = ({ label, value, mono }: { label: string; value: string; mono?: boolean }) => (
  <div className="w-done-row"><span className="w-tick"><I n="check" s={16} w={2.8} /></span><span className="w-done-l">{label}</span><span className={mono ? "w-mono w-strong" : "w-strong"}>{value}</span></div>
);

// ---------- scan field ----------
/** One input that a hardware scanner types into (ending with Enter), that you can type into, or fill from the camera. */
export function ScanField({ label, placeholder = "Scan or type", onScan, mono = true, busy, autoFocus = true, id, onType }: {
  label: string; placeholder?: string; onScan: (code: string) => void; mono?: boolean; busy?: boolean; autoFocus?: boolean; id?: string;
  /** Called as each character is typed, for suggestions. */
  onType?: (v: string) => void;
}) {
  const [v, setV] = useState("");
  const [cam, setCam] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  const fid = id ?? `scan-${label.replace(/\W+/g, "-").toLowerCase()}`;
  // Keep the field ready for the next scan: refocus after each one and when the page comes back.
  useEffect(() => { if (autoFocus && !busy) ref.current?.focus(); }, [busy, autoFocus, label]);
  const submit = (code: string) => { const c = code.trim(); if (!c || busy) return; setV(""); onType?.(""); onScan(c); };
  return (
    <div className="w-field">
      <label htmlFor={fid} className="w-label">{label}</label>
      <form className={`w-scan ${busy ? "busy" : ""}`} onSubmit={(e) => { e.preventDefault(); submit(v); }}>
        <input id={fid} ref={ref} value={v} onChange={(e) => { setV(e.target.value); onType?.(e.target.value); }} placeholder={placeholder} autoComplete="off" autoCapitalize="characters" spellCheck={false}
          enterKeyHint="go" className={mono ? "w-mono" : ""} disabled={busy} />
        {v.trim() ? <button type="submit" className="w-go" aria-label="Enter"><I n="arrow" /></button>
          : window.BarcodeDetector ? <button type="button" className="w-cam" aria-label="Scan with the camera" onClick={() => setCam(true)}><I n="camera" /></button> : null}
      </form>
      {cam && <Camera onClose={() => setCam(false)} onCode={(c) => { setCam(false); submit(c); }} />}
    </div>
  );
}

function Camera({ onClose, onCode }: { onClose: () => void; onCode: (c: string) => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let stream: MediaStream | null = null, stop = false;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
        if (!video.current) return;
        video.current.srcObject = stream; await video.current.play();
        const det = new window.BarcodeDetector!({ formats: ["code_128", "code_39", "ean_13", "ean_8", "upc_a", "upc_e", "qr_code", "data_matrix", "itf"] });
        while (!stop) {
          const found = await det.detect(video.current).catch(() => []);
          if (found[0]?.rawValue) { onCode(found[0].rawValue); return; }
          await new Promise((r) => setTimeout(r, 150));
        }
      } catch { setErr("The camera isn't available. Allow camera access, or use the scanner."); }
    })();
    return () => { stop = true; stream?.getTracks().forEach((t) => t.stop()); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="w-camera" role="dialog" aria-label="Camera scanner">
      <video ref={video} playsInline muted />
      <div className="w-camera-frame" aria-hidden="true" />
      {err && <div className="w-camera-err">{err}</div>}
      <button type="button" className="w-btn w-camera-close" onClick={onClose}>Cancel</button>
    </div>
  );
}

// ---------- quantity ----------
export function Stepper({ value, onChange, max, of, id, big }: { value: number; onChange: (n: number) => void; max?: number; of?: string; id: string; big?: boolean }) {
  const [text, setText] = useState(String(value));
  useEffect(() => { if (Number(text) !== value) setText(String(value)); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (n: number) => { const v = Math.max(0, max !== undefined ? Math.min(max, n) : n); onChange(Math.round(v * 1e5) / 1e5); };
  return (
    <div className={`w-stepper ${big ? "big" : ""}`}>
      <button type="button" aria-label="Less" onClick={() => set(value - 1)} disabled={value <= 0}><I n="minus" s={26} w={2.5} /></button>
      <div className="w-qty">
        <input id={id} inputMode="decimal" value={text} onChange={(e) => { setText(e.target.value); const n = Number(e.target.value); if (Number.isFinite(n)) set(n); }} onFocus={(e) => e.target.select()} />
        {of && <span className="w-of">{of}</span>}
      </div>
      <button type="button" aria-label="More" onClick={() => set(value + 1)} disabled={max !== undefined && value >= max}><I n="plus" s={26} w={2.5} /></button>
    </div>
  );
}

export const fmt = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 3 });
export const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));
export const errBody = (e: unknown): any => (e && typeof e === "object" && "body" in e ? (e as any).body : {});
